// 금천아 빌려줘 — 매출 현황 (2026-10-16)
//
// 카페24페이먼츠 거래내역은 결제 한 건에 대여료+배송비+보증금이 합쳐져 있다. 여기서 나눠 보고,
// 사람이 PG 엑셀과 대조한다(PG API 연동·회계 전표·부가세는 범위 밖).
//
// ⚠️ 계산은 전부 서버(rental/services/sales.py)가 한다 — 화면은 받은 숫자를 그대로 그리고
//    엑셀로 내보내기만 한다. 결제는 결제일, 환불은 환불일 기준이다.
//    예외: 연간 조회의 "N월 소계" 줄은 화면이 받은 줄을 달별로 더한 값이다(표시용 합계).
// 조회 조건은 URL에 남긴다 — ?mode=year&year=2026 · ?mode=month&month=2026-10 · ?mode=range&from=&to=
//    + &status=all|valid|active|done|canceled(상태 탭). mode가 없으면 연간(올해), status가 없으면 취소 제외 — salesFilters.js.
// 상태 탭은 서버가 거르고 요약도 다시 더한다(?status=) — 화면에서 거르지 않는다(엑셀과 숫자 일치).
// 분류 탭(2026-10-30, ?category=) — 상태 탭 아래 한 줄, 같은 방식(서버가 거른다). 탭 건수는 다른 쪽 선택을 반영한
//    서버 값(counts·category_counts). 전체 탭이면 요약 카드 아래 "분류별 순 매출"(서버 by_category). 표에는 분류 열이
//    없고 엑셀에만 있다. 분류가 빈 대여 상품이 있으면 맨 위 노란 안내(서버 uncategorized_products).
// 환불 예정(2026-10-19, 서버 *_refund_expected) — 진행 중 주문의 아직 안 돌려준 돈. 회색으로 따로 보이고
//    실제 환불 합계·순 매출에는 섞지 않는다. 실제 환불 값이 있으면 그 칸은 실제값만.
//    노란 줄(회수완료·환불 기록 없음)의 예정은 주황색(expected_urgent) — 지금 처리할 것.
// 순 매출 열(2026-10-19, 서버 row.net) = 요약 공식과 같은 줄별 값 — 줄 합 = 요약 순 매출.
// 입금 예정(2026-10-23, 서버 row.awaiting) — 입금대기 주문. 금액은 expected_* 칸(회색 "입금 예정 N원")이고
//    소계·합계·순 매출 카드에 넣지 않는다. 순 매출 카드 아래 "예정 반영 시"(환불 예정과 합친 한 줄)에만 반영.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";

import api from "../../api";
import { formatWon } from "./format";
import { CATEGORY_TABS, categoryLabel, periodParams, readFilters, STATUS_TABS, statusLabel } from "./salesFilters";
import "./rental.css";

const thisYear = () => new Date().getFullYear();
// 연도 선택칸 — 주문이 있는 첫 해(2026, 금천아 빌려줘 카페24 연동 시작)부터 올해까지.
const FIRST_YEAR = 2026;
const yearOptions = () => {
  const out = [];
  for (let y = Math.max(thisYear(), FIRST_YEAR); y >= FIRST_YEAR; y -= 1) out.push(String(y));
  return out;
};
const num = (v) => (v === null || v === undefined || v === "" ? null : Number(v));
// 관리자 "대여 상품" — 분류가 빈 상품만 걸러서 연다(사내망 전용 경로).
export const ADMIN_PRODUCTS_URL = "/gumcheon-control-2024/rental/rentalproduct/?category__exact=";
const mmdd = (iso) => (iso ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}` : "");
const paidLabel = (iso) => (iso ? `${iso.slice(0, 10)} ${iso.slice(11, 16)}` : "");
const dates = (list) => (list || []).map(mmdd).join(", ");
// 환불 칸 — "10/12 · 20,000원". 금액이 0이면 비운다.
const refundCell = (amount, list) =>
  num(amount) ? `${dates(list)}${list && list.length ? " · " : ""}${formatWon(amount)}` : "";

// 소계에 더하는 열 — 결제 쪽은 기간 안 결제만(기간 밖이면 null → 0), 환불 쪽은 그대로.
const SUM_KEYS = ["net", "net_after_shipping_expected", "net_after_expected", "payment", "deposit", "shipping", "rental",
  "deposit_refund", "deduction", "shipping_refund", "cancel_refund",
  "deposit_refund_expected", "shipping_refund_expected",
  "expected_payment", "expected_rental", "expected_shipping", "expected_deposit"];
// 소계·합계의 결제금액·대여료·배송비·보증금 칸 → 입금 예정 키(서버 줄·요약 같은 이름)
const AWAITING_COLS = [["payment", "expected_payment"], ["rental", "expected_rental"],
  ["shipping", "expected_shipping"], ["deposit", "expected_deposit"]];

// 음수는 "−10,000원"(환불만 있는 줄의 순 매출).
const signedWon = (v) => (num(v) < 0 ? `−${formatWon(-num(v))}` : formatWon(v));

// 주문 줄의 환불 칸 — 실제값이 있으면 실제값, 없으면 "예정 −20,000원"(회색, 노란 줄은 주황), 둘 다 없으면 빈칸.
function RefundCell({ amount, list, expected, urgent }) {
  if (num(amount)) return refundCell(amount, list);
  if (num(expected)) {
    const cls = `rental-sales-expected${urgent ? " rental-sales-expected--urgent" : ""}`;
    return <span className={cls}>예정 −{formatWon(expected)}</span>;
  }
  return "";
}

// 순 매출 칸 — 굵게, 배송비 환불 예정이 있으면 아래 회색 작은 글씨 "예정 반영 시 N원".
function NetCell({ net, after, shippingExpected }) {
  return (
    <>
      <strong>{signedWon(net)}</strong>
      {num(shippingExpected) ? <div className="rental-sales-net-after">예정 반영 시 {signedWon(after)}</div> : null}
    </>
  );
}

// 소계·연간 합계 줄의 순 매출 칸 — 숫자는 지금 모양 그대로, 배송비 환불 예정이나 입금 예정이 있으면 주문 줄과 같은
// 회색 작은 글씨 "예정 반영 시 N원". 값은 순 매출 카드와 같은 공식(환불 예정 빼고 입금 예정 더함, 서버 net_after_expected
// 줄 합 — 2026-10-23). 둘 다 없으면 붙이지 않는다. 글자는 위 숫자와 같은 크기·굵기, 회색(카드처럼, 2026-10-23).
function SubtotalNetCell({ sums }) {
  return (
    <>
      {signedWon(sums.net)}
      {num(sums.shipping_refund_expected) || sums.awaiting
        ? <div className="rental-sales-net-after rental-sales-net-after--sum">예정 반영 시 {signedWon(sums.net_after_expected)}</div>
        : null}
    </>
  );
}

// 소계·연간 합계 줄의 결제금액·대여료·배송비·보증금 칸 — 위 숫자 그대로, 그 묶음에 입금 예정 줄이 있으면 아래 회색
// "예정 반영 시 N원"(= 칸 합 + 입금 예정 줄 금액, 2026-10-23). 입금 예정이 없으면 붙이지 않는다.
function SubtotalAmountCell({ sums, col, expectedKey }) {
  return (
    <>
      {formatWon(sums[col])}
      {sums.awaiting
        ? <div className="rental-sales-net-after rental-sales-net-after--sum">
            예정 반영 시 {formatWon((num(sums[col]) || 0) + (num(sums[expectedKey]) || 0))}
          </div>
        : null}
    </>
  );
}

// 소계 줄의 환불 칸 — 실제 합계(검은 글씨)와 회색 "예정 N원"을 나란히(섞지 않는다).
function SubtotalRefundCell({ amount, expected }) {
  return (
    <>
      {amount ? formatWon(amount) : ""}
      {amount && expected ? " " : ""}
      {expected ? <span className="rental-sales-expected">예정 {formatWon(expected)}</span> : ""}
    </>
  );
}

// 줄이 속한 달 — 기간 안 결제면 결제월, 아니면(환불만 있는 줄) 첫 환불일의 달.
const rowMonth = (r) => (r.awaiting ? r.ordered_at || ""
  : r.paid_in_period ? r.paid_at : (r.refund_dates || [])[0] || r.paid_at || "").slice(0, 7);

/** 연간 표 — 달별로 묶고 각 달 끝에 소계 줄. 추가 주문(들여쓴 줄)은 원 주문과 같은 달에 둔다. */
function withMonthlySubtotals(rows) {
  const groups = new Map();
  let parentKey = "";
  rows.forEach((r) => {
    const key = r.indent ? parentKey : rowMonth(r);
    if (!r.indent) parentKey = key;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(r);
  });
  const out = [];
  [...groups.keys()].sort().forEach((key) => {
    const list = groups.get(key);
    const sums = Object.fromEntries(SUM_KEYS.map((k) => [k, list.reduce((a, r) => a + (num(r[k]) || 0), 0)]));
    sums.awaiting = list.filter((r) => r.awaiting).length;          // 입금 예정 줄 수 — "예정 반영 시" 표시 여부
    list.forEach((r) => out.push({ type: "row", row: r }));
    out.push({ type: "subtotal", key, label: `${Number(key.slice(5, 7))}월 소계`, sums });
  });
  return out;
}

const PAYMENT_ROWS = [
  ["rental", "대여료"],
  ["shipping", "배송비"],
  ["deposit", "보증금 수령"],
  ["payment", "합계 (결제 총액)"],
];
const REFUND_ROWS = [
  ["deposit_refund", "보증금 환불"],
  ["deduction", "보증금 차감 (매출 전환)"],
  ["shipping_refund", "배송비 환불"],
  ["cancel_refund", "주문 취소 환불"],
];

export default function RentalSales() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // 처음 값은 URL에서(새로고침해도 유지), 없으면 연간(올해)·전체 탭.
  const [initial] = useState(() => readFilters(searchParams));
  const [mode, setMode] = useState(initial.mode);
  const [year, setYear] = useState(initial.year);
  const [month, setMonth] = useState(initial.month);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [status, setStatus] = useState(initial.status);
  const [category, setCategory] = useState(initial.category);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const params = useMemo(
    () => ({ ...periodParams({ mode, year, month, from, to }), status, category }),
    [mode, year, month, from, to, status, category],
  );

  // 조회 조건을 URL에 — 기록을 쌓지 않고 바꿔 쓴다(뒤로 가기가 필터 변경마다 걸리지 않게).
  useEffect(() => {
    setSearchParams({ mode, ...params }, { replace: true });
  }, [mode, params, setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("rental/sales/", { params });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "매출 현황을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = data?.summary || {};
  const rows = data?.rows || [];
  const counts = data?.counts || {};
  // 받은 응답의 탭 — 탭을 바꾼 직후 옛 응답에 새 탭 이름을 붙이지 않게(엑셀 파일명·구분 줄).
  const shownStatus = data?.status || "all";
  const shownCategory = data?.category || "all";
  const categoryCounts = data?.category_counts || {};
  const byCategory = data?.by_category || [];
  const uncategorized = data?.uncategorized_products || [];
  // 연간일 때만 달별 소계 줄 — 받은 응답이 연간 조회의 것일 때만(모드를 바꾼 직후 옛 응답에 붙이지 않게).
  const yearly = mode === "year" && Boolean(data?.period?.from?.endsWith("-01-01"))
    && Boolean(data?.period?.to?.endsWith("-12-31"));
  const items = yearly ? withMonthlySubtotals(rows) : rows.map((row) => ({ type: "row", row }));
  // 연간 합계 줄 — 서버 요약 값 그대로(요약 카드와 같은 숫자). 월별 소계 합과도 같다(서버 테스트).
  const yearTotal = Object.fromEntries(SUM_KEYS.map((k) => [k, num(summary[k]) || 0]));
  yearTotal.awaiting = num(summary.awaiting_count) || 0;

  const exportExcel = () => {
    if (!data) return;
    // 주문번호가 첫 열 — PG 엑셀과 VLOOKUP으로 맞춘다. 금액은 숫자 그대로(엑셀에서 더할 수 있게).
    const header = [
      "주문번호", "결제일", "주문자", "상품", "분류", "결제수단", "순 매출", "예정 반영 시 순 매출", "결제금액", "대여료",
      "배송비", "보증금",
      "상태", "보증금 환불일", "보증금 환불", "차감액", "배송비 환불일", "배송비 환불",
      "취소 환불일", "취소 환불", "보증금 환불 예정", "배송비 환불 예정", "PG 대조", "비고",
    ];
    const subtotalLine = (it) => [
      it.label, "", "", "", "", "", it.sums.net,
      it.sums.shipping_refund_expected || it.sums.awaiting ? it.sums.net_after_expected : null,
      it.sums.payment, it.sums.rental, it.sums.shipping, it.sums.deposit, "",
      "", it.sums.deposit_refund || null, it.sums.deduction || null, "", it.sums.shipping_refund || null,
      "", it.sums.cancel_refund || null, it.sums.deposit_refund_expected || null,
      it.sums.shipping_refund_expected || null, "", "소계",
    ];
    const rowLine = (r) => [
      r.order_id, r.paid_in_period ? paidLabel(r.paid_at) : "", r.buyer_name, r.product_name,
      r.category_label, r.payment_method_name, num(r.net),
      num(r.shipping_refund_expected) ? num(r.net_after_shipping_expected) : null,
      num(r.payment), num(r.rental), num(r.shipping), num(r.deposit),
      r.status_label, (r.deposit_refund_dates || []).join(", "), num(r.deposit_refund) || null,
      num(r.deduction) || null, (r.shipping_refund_dates || []).join(", "), num(r.shipping_refund) || null,
      (r.cancel_refund_dates || []).join(", "), num(r.cancel_refund) || null,
      num(r.deposit_refund_expected) || null, num(r.shipping_refund_expected) || null, r.pg_check,
      [r.awaiting ? `입금 예정 ${num(r.expected_payment).toLocaleString("ko-KR")}원` : "",
        r.indent ? "추가 주문" : "", r.is_test ? "테스트" : "", r.auto_refund ? "환불 자동(원본)" : "",
        r.missing_deposit_refund ? "보증금 환불 기록 없음(예정 = 지금 처리)" : "",
        r.paid_in_period || r.awaiting ? "" : "결제는 기간 밖"].filter(Boolean).join(", "),
    ];
    // 입금 예정이 있는 소계·합계 바로 아래 — 화면의 회색 "예정 반영 시"와 같은 값
    const expectedLine = (it) => {
      const line = Array(header.length).fill("");
      line[0] = `${it.label} (예정 반영 시)`;
      line[7] = it.sums.net_after_expected;                  // "예정 반영 시 순 매출" 열(소계 줄과 같은 자리)
      AWAITING_COLS.forEach(([col, key], i) => { line[8 + i] = (num(it.sums[col]) || 0) + (num(it.sums[key]) || 0); });
      line[header.length - 1] = "입금 예정 포함";
      return line;
    };
    const subtotalLines = (it) => (it.sums.awaiting ? [subtotalLine(it), expectedLine(it)] : [subtotalLine(it)]);
    // 연간이면 소계 줄과 맨 끝 "연간 합계" 줄(요약 카드 값)도 그대로(화면과 같은 순서)
    const body = items.flatMap((it) => (it.type === "subtotal" ? subtotalLines(it) : [rowLine(it.row)]));
    if (yearly) body.push(...subtotalLines({ label: "연간 합계", sums: yearTotal }));
    const s = summary;
    const summarySheet = [
      ["구분", statusLabel(shownStatus)],
      ["분류", categoryLabel(shownCategory)],
      ["기간", `${data.period.from} ~ ${data.period.to}`],
      [],
      ["결제 (결제일 기준)"],
      ...PAYMENT_ROWS.map(([k, label]) => [label, num(s[k])]),
      ["  PG 결제 (카페24페이먼츠 승인 합계와 대조)", num(s.pg)],
      ["  무통장", num(s.cash)],
      [],
      ["환불 (환불일 기준)"],
      ...REFUND_ROWS.map(([k, label]) => [label, num(s[k])]),
      ["  보증금 환불 예정 (진행 중 주문, 환불 합계에 미포함)", num(s.deposit_refund_expected)],
      ["  배송비 환불 예정 (직접 수령·반납, 환불 합계에 미포함)", num(s.shipping_refund_expected)],
      [],
      ["순 매출", num(s.net)],
      ["  = 대여료 + 배송비 − 배송비 환불 − 주문 취소 환불 + 보증금 차감 (예정은 빼지 않음)"],
      ["  배송비 환불 예정 반영 시", num(s.net_after_shipping_expected)],
      ["  입금 예정 (입금대기 주문, 결제·순 매출에 미포함)", num(s.awaiting_amount), `${num(s.awaiting_count) || 0}건`],
      ["  입금 예정 대여료 (보증금 제외 — 순 매출에 더해지는 몫)", num(s.awaiting_net)],
      ["  환불 예정·입금 예정 반영 시", num(s.net_after_expected)],
      ...(shownCategory === "all" && byCategory.length
        ? [[], ["분류별 순 매출", "순 매출", "건수", "예정 반영 시"],
          ...byCategory.map((c) => [`  ${c.label}`, num(c.net), c.count, num(c.net_after_expected)])]
        : []),
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([header, ...body]), "주문별");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summarySheet), "요약");
    const name = mode === "year" ? `${year}년` : mode === "month" ? month : `${from}_${to}`;
    const tag = [statusLabel(shownStatus), shownCategory === "all" ? "" : categoryLabel(shownCategory)]
      .filter(Boolean).join("_").replace(/ /g, "");
    XLSX.writeFile(wb, `대여_매출현황_${name}_${tag}.xlsx`);
  };

  return (
    <div className="rental-page rental-sales">
      <div className="rental-header">
        <h2 className="rental-title">매출 현황</h2>
        <div className="rental-header-right">
          <button type="button" className="rental-btn-sync" onClick={() => navigate("/rental/orders")}>
            주문 목록
          </button>
          <button type="button" className="rental-btn-sync" onClick={exportExcel} disabled={!data || loading}>
            엑셀
          </button>
        </div>
      </div>

      {/* 필터 한 줄 — [월][YYYY-MM]  [기간][시작] ~ [끝]. 폰 폭에서는 월 묶음 / 기간 묶음 두 줄로 접힌다
          (묶음 안은 줄바꿈 안 함). 입력칸은 고정 폭 — 전역 input 규칙에 끌려 가로로 늘지 않게. */}
      <div className="rental-sales-filter">
        <div className="rental-sales-group">
          <label className="rental-sales-radio">
            <input type="radio" checked={mode === "year"} onChange={() => setMode("year")} />
            연간
          </label>
          <select
            className="rental-sales-input rental-sales-input--year"
            value={year}
            disabled={mode !== "year"}
            onChange={(e) => setYear(e.target.value)}
          >
            {yearOptions().map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div className="rental-sales-group">
          <label className="rental-sales-radio">
            <input type="radio" checked={mode === "month"} onChange={() => setMode("month")} />
            월
          </label>
          <input
            type="month"
            className="rental-sales-input rental-sales-input--month"
            value={month}
            disabled={mode !== "month"}
            onChange={(e) => e.target.value && setMonth(e.target.value)}
          />
        </div>
        <div className="rental-sales-group">
          <label className="rental-sales-radio">
            <input type="radio" checked={mode === "range"} onChange={() => setMode("range")} />
            기간
          </label>
          <input type="date" className="rental-sales-input rental-sales-input--date" value={from}
                 disabled={mode !== "range"} onChange={(e) => e.target.value && setFrom(e.target.value)} />
          <span className="rental-sales-tilde">~</span>
          <input type="date" className="rental-sales-input rental-sales-input--date" value={to}
                 disabled={mode !== "range"} onChange={(e) => e.target.value && setTo(e.target.value)} />
        </div>
        <span className="rental-sales-basis">결제는 결제일, 환불은 환불일 기준</span>
      </div>

      {error && <div className="rental-error">{error}</div>}

      {/* 분류가 빈 대여 상품 — 그 상품 주문은 "미분류"라 전체 탭에만 잡힌다. */}
      {uncategorized.length > 0 && (
        <div className="rental-sales-notice" role="note">
          분류 미지정 상품 {uncategorized.length}개
          <span className="rental-sales-notice-names">
            ({uncategorized.map((p) => `${p.product_no} ${p.name}`).join(", ")})
          </span>
          {" — "}
          <a href={ADMIN_PRODUCTS_URL} target="_blank" rel="noreferrer">대여 상품 설정에서 지정</a>
        </div>
      )}

      {data && (
        <div className="rental-sales-summary">
          <div className="rental-sales-box">
            <div className="rental-sales-box-title">결제</div>
            {PAYMENT_ROWS.map(([k, label]) => (
              <div className={`rental-sales-line${k === "payment" ? " rental-sales-line--total" : ""}`} key={k}>
                <span>{label}</span>
                <strong>{formatWon(summary[k])}</strong>
              </div>
            ))}
            <div className="rental-sales-sub">
              결제 총액 = PG 결제 <strong>{formatWon(summary.pg)}</strong> + 무통장{" "}
              <strong>{formatWon(summary.cash)}</strong>
            </div>
            <div className="rental-sales-hint">PG 결제가 카페24페이먼츠 승인 합계와 같아야 합니다</div>
            {/* 입금 예정 — 입금대기 주문, 위 합계에 섞지 않는다 */}
            <div className="rental-sales-line rental-sales-line--expected">
              <span>입금 예정 {num(summary.awaiting_count) || 0}건</span>
              <span>{formatWon(summary.awaiting_amount)}</span>
            </div>
          </div>
          <div className="rental-sales-box">
            <div className="rental-sales-box-title">환불</div>
            {REFUND_ROWS.map(([k, label]) => (
              <div className="rental-sales-line" key={k}>
                <span>{label}</span>
                <strong>{formatWon(summary[k])}</strong>
              </div>
            ))}
            {/* 예정 — 진행 중 주문만, 위 합계에 섞지 않는다 */}
            <div className="rental-sales-line rental-sales-line--expected">
              <span>보증금 환불 예정</span>
              <span>{formatWon(summary.deposit_refund_expected)}</span>
            </div>
            <div className="rental-sales-line rental-sales-line--expected">
              <span>배송비 환불 예정</span>
              <span>{formatWon(summary.shipping_refund_expected)}</span>
            </div>
          </div>
          <div className="rental-sales-box rental-sales-box--net">
            <div className="rental-sales-box-title">순 매출</div>
            <div className="rental-sales-net">{formatWon(summary.net)}</div>
            <div className="rental-sales-hint">
              대여료 + 배송비 − 배송비 환불 − 주문 취소 환불 + 보증금 차감
            </div>
            {/* 환불 예정·입금 예정 반영 시 — 둘 다 있으면 한 줄로 합친다(서버 net_after_expected) */}
            {num(summary.shipping_refund_expected) || num(summary.awaiting_count) ? (
              <div className="rental-sales-net-expected">
                <div className="rental-sales-hint">
                  {[
                    num(summary.shipping_refund_expected)
                      ? `배송비 환불 예정 ${formatWon(summary.shipping_refund_expected)}` : "",
                    num(summary.awaiting_count) ? `입금 예정 대여료 ${formatWon(summary.awaiting_net)}(보증금 제외)` : "",
                  ].filter(Boolean).join(" · ")} 반영 시
                </div>
                <div className="rental-sales-net rental-sales-net--expected">
                  {formatWon(summary.net_after_expected)}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {data && shownCategory === "all" && byCategory.length > 0 && (
        <div className="rental-sales-bycat">
          <div className="rental-sales-box-title">분류별 순 매출</div>
          <table className="rental-sales-bycat-table" aria-label="분류별 순 매출">
            <tbody>
              {byCategory.map((c) => (
                <tr key={c.category || "none"}>
                  <th>{c.label}</th>
                  <td className="rental-sales-num">{c.count}건</td>
                  <td className="rental-sales-num rental-sales-bycat-net">{signedWon(c.net)}</td>
                  <td className="rental-sales-num">
                    {num(c.shipping_refund_expected) || num(c.awaiting_count)
                      ? <span className="rental-sales-expected">예정 반영 시 {signedWon(c.net_after_expected)}</span>
                      : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 상태 탭 — 표 바로 위. 건수는 서버 counts(지금 분류 안의 줄 수). */}
      {data && (
        <div className="rental-tabs rental-sales-tabs">
          {STATUS_TABS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`rental-tab${status === key ? " rental-tab--active" : ""}`}
              onClick={() => setStatus(key)}
            >
              {label}
              <span className="rental-tab-count">({counts[key] ?? 0})</span>
            </button>
          ))}
        </div>
      )}
      {/* 분류 탭 — 상태 탭 바로 아래. 건수는 서버 category_counts(지금 상태 안의 줄 수). */}
      {data && (
        <div className="rental-tabs rental-sales-tabs rental-sales-category-tabs" role="group" aria-label="분류">
          {CATEGORY_TABS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`rental-tab${category === key ? " rental-tab--active" : ""}`}
              onClick={() => setCategory(key)}
            >
              {label}
              <span className="rental-tab-count">({categoryCounts[key] ?? 0})</span>
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="rental-empty">불러오는 중…</div>
      ) : data && rows.length === 0 ? (
        <div className="rental-empty">
          {shownStatus === "all" && shownCategory === "all"
            ? "이 기간에 결제·환불이 없습니다."
            : `이 기간에 "${[shownStatus === "all" ? "" : statusLabel(shownStatus),
              shownCategory === "all" ? "" : categoryLabel(shownCategory)].filter(Boolean).join(" · ")}" 주문이 없습니다.`}
        </div>
      ) : data ? (
        <>
          {["all", "valid", "active"].includes(shownStatus) && (
            <div className="rental-sales-legend">
              <span className="rental-sales-swatch" /> 회수완료인데 보증금 환불 기록이 없는 주문 — 주황 "예정" 금액을 지금 환불
            </div>
          )}
          <div className="rental-sales-table-wrap">
            <table className="rental-sales-table">
              <thead>
                <tr>
                  <th>주문번호</th><th>결제일</th><th>주문자</th><th>상품</th><th>결제수단</th>
                  <th className="rental-sales-net-col">순 매출</th><th>결제금액</th><th>대여료</th><th>배송비</th><th>보증금</th><th>상태</th>
                  <th>보증금 환불</th><th>차감액</th><th>배송비 환불</th><th>취소 환불</th><th>PG 대조</th>
                </tr>
              </thead>
              <tbody>
                {[...items, ...(yearly ? [{ type: "subtotal", key: "year", label: "연간 합계", sums: yearTotal }] : [])]
                  .map(({ type, row: r, ...sub }) => type === "subtotal" ? (
                  <tr key={`sub-${sub.key}`}
                      className={`rental-sales-row--subtotal${sub.key === "year" ? " rental-sales-row--total" : ""}`}>
                    <td colSpan={5}>{sub.label}</td>
                    <td className="rental-sales-net-col"><SubtotalNetCell sums={sub.sums} /></td>
                    {AWAITING_COLS.map(([col, expectedKey]) => (
                      <td className="rental-sales-num" key={col}>
                        <SubtotalAmountCell sums={sub.sums} col={col} expectedKey={expectedKey} />
                      </td>
                    ))}
                    <td />
                    <td className="rental-sales-num">
                      <SubtotalRefundCell amount={sub.sums.deposit_refund} expected={sub.sums.deposit_refund_expected} />
                    </td>
                    <td className="rental-sales-num">{sub.sums.deduction ? formatWon(sub.sums.deduction) : ""}</td>
                    <td className="rental-sales-num">
                      <SubtotalRefundCell amount={sub.sums.shipping_refund} expected={sub.sums.shipping_refund_expected} />
                    </td>
                    <td className="rental-sales-num">{sub.sums.cancel_refund ? formatWon(sub.sums.cancel_refund) : ""}</td>
                    <td />
                  </tr>
                ) : (
                  <tr key={r.id} className={r.awaiting ? "rental-sales-row--awaiting"
                    : r.missing_deposit_refund ? "rental-sales-row--missing" : ""}>
                    <td className={r.indent ? "rental-sales-indent" : ""}>
                      <button type="button" className="rental-sales-link" onClick={() => navigate(`/rental/orders/${r.id}`)}>
                        {r.indent ? "└ " : ""}{r.order_id}
                      </button>
                      {r.is_test && <span className="rental-sales-tag">테스트</span>}
                    </td>
                    <td>{r.paid_in_period ? paidLabel(r.paid_at)
                      : <span className="rental-sales-muted">{r.awaiting ? "입금 전" : "기간 밖"}</span>}</td>
                    <td>{r.buyer_name}</td>
                    <td>{r.product_name}</td>
                    <td>{r.payment_method_name}</td>
                    {r.awaiting ? (
                      <>
                        <td className="rental-sales-net-col"><span className="rental-sales-expected">예정</span></td>
                        <td className="rental-sales-num">
                          <span className="rental-sales-expected">입금 예정 {formatWon(r.expected_payment)}</span>
                        </td>
                        <td className="rental-sales-num"><span className="rental-sales-expected">{formatWon(r.expected_rental)}</span></td>
                        <td className="rental-sales-num"><span className="rental-sales-expected">{formatWon(r.expected_shipping)}</span></td>
                        <td className="rental-sales-num"><span className="rental-sales-expected">{formatWon(r.expected_deposit)}</span></td>
                        <td><span className="rental-sales-badge-awaiting">{r.status_label}</span></td>
                      </>
                    ) : (
                      <>
                        <td className="rental-sales-net-col">
                          <NetCell net={r.net} after={r.net_after_shipping_expected} shippingExpected={r.shipping_refund_expected} />
                        </td>
                        <td className="rental-sales-num">{r.paid_in_period ? formatWon(r.payment) : ""}</td>
                        <td className="rental-sales-num">{r.paid_in_period ? formatWon(r.rental) : ""}</td>
                        <td className="rental-sales-num">{r.paid_in_period ? formatWon(r.shipping) : ""}</td>
                        <td className="rental-sales-num">{r.paid_in_period ? formatWon(r.deposit) : ""}</td>
                        <td>{r.status_label}</td>
                      </>
                    )}
                    <td className="rental-sales-num">
                      <RefundCell amount={r.deposit_refund} list={r.deposit_refund_dates}
                                  expected={r.deposit_refund_expected} urgent={r.expected_urgent} />
                    </td>
                    <td className="rental-sales-num">{num(r.deduction) ? formatWon(r.deduction) : ""}</td>
                    <td className="rental-sales-num">
                      <RefundCell amount={r.shipping_refund} list={r.shipping_refund_dates}
                                  expected={r.shipping_refund_expected} urgent={r.expected_urgent} />
                    </td>
                    <td className="rental-sales-num">{refundCell(r.cancel_refund, r.cancel_refund_dates)}</td>
                    <td>{r.pg_check}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="rental-sales-hint">
            PG 대조: ○ = 카드 부분취소(PG 거래내역에 있음) · PG 없음 = 계좌이체 · (자동) = 환불 기록 없이 카페24
            결제금액 감소로 계산한 값 — 주문 상세에서 환불 기록을 저장하면 그 값으로 바뀝니다.
            <br />
            <span className="rental-sales-expected">예정</span> = 진행 중 주문의 아직 돌려주지 않은 보증금(직접 수령·반납은
            배송비 포함) · <span className="rental-sales-expected rental-sales-expected--urgent">예정</span> = 회수완료했는데
            환불 기록이 없는 주문 — 지금 처리할 것. 둘 다 환불 합계·순 매출에는 들어가지 않습니다.
            <br />
            <span className="rental-sales-badge-awaiting">입금대기</span> = 무통장 입금 전 주문(주문일 기준) — 금액은 카페24
            주문금액을 회색 "입금 예정"으로만 보이고 소계·합계·순 매출에는 넣지 않습니다. 입금이 확인되면 일반 줄이 됩니다.
            <br />
            순 매출 = 대여료 + 배송비 − 배송비 환불 − 주문 취소 환불 + 보증금 차감(보증금 수령·환불은 빠짐) — 이 기간에
            생긴 금액만이라 환불만 있는 줄은 음수입니다.
          </div>
        </>
      ) : null}
    </div>
  );
}
